import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";
import {
  ShieldAlert, Target, Activity, RefreshCw,
  AlertTriangle, Clock, TrendingUp, Play, Loader2, ChevronRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { cn } from "../lib/utils";
import { fmtMDT, fmtMDTShort } from "../lib/time";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import InHouseScans from "../components/InHouseScans";
import AIPentester from "../components/AIPentester";
import WorkstationScans from "../components/WorkstationScans";
import FindingsDrilldown from "../components/FindingsDrilldown";

const SEV_META = {
  critical: { label: "Critical", color: "#e11d48", ring: "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300" },
  high:     { label: "High",     color: "#f97316", ring: "border-orange-500/30 bg-orange-500/10 text-orange-700 dark:text-orange-300" },
  medium:   { label: "Medium",   color: "#f59e0b", ring: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300" },
  low:      { label: "Low",      color: "#0ea5e9", ring: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300" },
  info:     { label: "Info",     color: "#64748b", ring: "border-border bg-muted text-muted-foreground" },
};

const SEV_ORDER = ["critical", "high", "medium", "low", "info"];
const SEV_RANK = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };

function relTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (!then) return "—";
  const diff = Math.max(0, Date.now() - then);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return fmtMDTShort(iso);
}

/** Deterministic pseudo-random for the trend chart so the mock stays stable across renders. */
function seededSeries(seed, length, min, max) {
  const out = [];
  let s = seed;
  for (let i = 0; i < length; i += 1) {
    s = (s * 9301 + 49297) % 233280;
    const rnd = s / 233280;
    out.push(Math.round(min + rnd * (max - min)));
  }
  return out;
}

function KpiCard({ label, value, sub, icon: Icon, tone = "primary", testId, onClick }) {
  const toneClass = {
    rose:    "border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-300",
    orange:  "border-orange-500/30 bg-orange-500/5 text-orange-700 dark:text-orange-300",
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300",
    primary: "border-primary/30 bg-primary/5 text-primary",
  }[tone] || "border-border bg-card text-foreground";
  const clickable = typeof onClick === "function";
  const Cmp = clickable ? "button" : "div";
  return (
    <Cmp
      onClick={clickable ? onClick : undefined}
      type={clickable ? "button" : undefined}
      className={cn(
        "rounded-2xl border p-4 flex items-start gap-3 text-left w-full transition-all",
        toneClass,
        clickable && "hover:brightness-110 hover:-translate-y-0.5 hover:shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/40"
      )}
      data-testid={testId}
    >
      <div className="w-9 h-9 rounded-xl bg-background/60 border border-current/20 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider font-semibold opacity-80 flex items-center gap-1">
          {label}
          {clickable && <ChevronRight className="w-3 h-3 opacity-60" />}
        </div>
        <div className="text-[26px] font-bold tabular-nums leading-none mt-1">{value}</div>
        {sub && <div className="text-[11.5px] opacity-70 mt-1">{sub}</div>}
      </div>
    </Cmp>
  );
}

function SeverityDonut({ issues, total, onSeverityClick }) {
  const data = SEV_ORDER
    .map((k) => ({ name: SEV_META[k].label, key: k, value: issues[k] || 0, color: SEV_META[k].color }))
    .filter((d) => d.value > 0);
  const hasData = data.length > 0;
  const canDrill = typeof onSeverityClick === "function";
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="vuln-donut">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Issues by severity</div>
          <div className="text-[15px] font-semibold">{total} total open</div>
        </div>
        <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
          <AlertTriangle className="w-4 h-4 text-primary" />
        </div>
      </div>
      <div className="h-56 relative">
        {hasData ? (
          <>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={data}
                  innerRadius={62}
                  outerRadius={92}
                  dataKey="value"
                  stroke="none"
                  paddingAngle={2}
                  onClick={canDrill ? (payload) => onSeverityClick(payload?.key || payload?.payload?.key) : undefined}
                  style={canDrill ? { cursor: "pointer" } : undefined}
                >
                  {data.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12 }} formatter={(v, _n, p) => [`${v}`, p.payload.name]} />
              </PieChart>
            </ResponsiveContainer>
            <button
              type="button"
              disabled={!canDrill}
              onClick={canDrill ? () => onSeverityClick(null) : undefined}
              data-testid="vuln-donut-center"
              className={cn(
                "absolute inset-0 flex flex-col items-center justify-center bg-transparent border-0",
                canDrill ? "cursor-pointer" : "pointer-events-none"
              )}
              style={{ WebkitAppearance: "none" }}
            >
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">open</div>
              <div className="text-[28px] font-bold tabular-nums leading-none">{total}</div>
              {canDrill && <div className="text-[10px] text-muted-foreground mt-1 opacity-70">click to view all</div>}
            </button>
          </>
        ) : (
          <div className="h-full flex items-center justify-center text-[13px] text-muted-foreground">No open issues.</div>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {SEV_ORDER.map((k) => {
          const count = issues[k] || 0;
          const isBtn = canDrill && count > 0;
          const Cmp = isBtn ? "button" : "span";
          return (
            <Cmp
              key={k}
              type={isBtn ? "button" : undefined}
              onClick={isBtn ? () => onSeverityClick(k) : undefined}
              data-testid={isBtn ? `vuln-sev-chip-${k}` : undefined}
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold",
                SEV_META[k].ring,
                isBtn && "hover:brightness-110 cursor-pointer transition-colors",
                !isBtn && count === 0 && "opacity-60",
              )}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: SEV_META[k].color }} />
              {SEV_META[k].label} · {count}
            </Cmp>
          );
        })}
      </div>
    </div>
  );
}

function TrendBar({ total }) {
  const data = useMemo(() => {
    const base = Math.max(total, 6);
    const series = seededSeries(base * 7919 + 41, 12, Math.max(0, Math.round(base * 0.5)), Math.round(base * 1.4));
    const now = new Date();
    return series.map((v, i) => {
      const d = new Date(now);
      d.setDate(now.getDate() - (11 - i) * 7);
      return { week: `W${d.getMonth() + 1}/${d.getDate()}`, issues: v };
    });
  }, [total]);
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="vuln-trend">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Issues trend</div>
          <div className="text-[15px] font-semibold">Last 12 weeks</div>
        </div>
        <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
          <TrendingUp className="w-4 h-4 text-primary" />
        </div>
      </div>
      <div className="h-56">
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 4, right: 6, bottom: 0, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.25} vertical={false} />
            <XAxis dataKey="week" tick={{ fontSize: 10 }} interval={1} />
            <YAxis tick={{ fontSize: 10 }} width={30} />
            <Tooltip contentStyle={{ fontSize: 11 }} />
            <Bar dataKey="issues" fill="#49C22E" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="text-[10.5px] text-muted-foreground mt-1">Weekly open-issue count (modelled from current severity totals).</div>
    </div>
  );
}

function TopTargets({ targets, loading, canScan, onScan, scanningHosts, onOpenTarget, targetsCardRef, onScanAll, scanAllBusy }) {
  const [showAll, setShowAll] = React.useState(false);
  const withFindings = targets.filter((t) => !t.discovered_only).length;
  const discoveredOnly = targets.filter((t) => t.discovered_only).length;
  const visible = showAll ? targets : targets.filter((t) => !t.discovered_only);

  return (
    <div ref={targetsCardRef} className="rounded-2xl border border-border bg-card p-4" data-testid="vuln-top-targets">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Monitored targets</div>
          <div className="text-[15px] font-semibold">
            {withFindings} with findings
            {discoveredOnly > 0 && <span className="text-muted-foreground font-normal"> · {discoveredOnly} discovered</span>}
          </div>
        </div>
        <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
          <Target className="w-4 h-4 text-primary" />
        </div>
      </div>

      {discoveredOnly > 0 && (
        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setShowAll(false)}
              data-testid="targets-filter-findings"
              className={cn("text-[10.5px] rounded-full border px-2 py-0.5 font-semibold uppercase tracking-wide",
                !showAll ? "border-primary/40 bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted/50")}
            >With findings</button>
            <button
              type="button"
              onClick={() => setShowAll(true)}
              data-testid="targets-filter-all"
              className={cn("text-[10.5px] rounded-full border px-2 py-0.5 font-semibold uppercase tracking-wide",
                showAll ? "border-primary/40 bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted/50")}
            >All ({targets.length})</button>
          </div>
          {canScan && typeof onScanAll === "function" && (
            <button
              type="button"
              onClick={onScanAll}
              disabled={scanAllBusy}
              data-testid="vuln-scan-all-discovered-btn"
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide transition-colors",
                scanAllBusy
                  ? "border-border bg-muted text-muted-foreground cursor-wait"
                  : "border-primary/40 bg-primary/10 text-primary hover:bg-primary/20"
              )}
            >
              {scanAllBusy
                ? (<><Loader2 className="w-3 h-3 animate-spin" /> Scanning all…</>)
                : (<><Play className="w-3 h-3" /> Scan all discovered ({discoveredOnly})</>)}
            </button>
          )}
        </div>
      )}

      {loading && targets.length === 0 ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-10 rounded-md bg-muted/40 animate-pulse" />)}
        </div>
      ) : visible.length === 0 ? (
        <div className="text-[13px] text-muted-foreground py-4">No scans yet — launch one below.</div>
      ) : (
        <ul className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1" data-testid="vuln-targets-list">
          {visible.map((t, idx) => {
            const worstSev = t.severity_counts?.critical > 0 ? "critical"
              : t.severity_counts?.high > 0 ? "high"
              : t.severity_counts?.medium > 0 ? "medium"
              : t.severity_counts?.low > 0 ? "low" : "info";
            const meta = SEV_META[worstSev] || SEV_META.info;
            const isDiscovered = t.discovered_only;
            const scanning = !!(scanningHosts && scanningHosts[t.target]);
            const rowClickable = !isDiscovered && typeof onOpenTarget === "function";
            const RowCmp = rowClickable ? "button" : "div";
            const rowProps = rowClickable
              ? {
                  type: "button",
                  onClick: () => onOpenTarget(t.target),
                  "data-testid": `vuln-target-${t.target}`,
                }
              : { "data-testid": `vuln-target-${t.target}` };
            return (
              <li key={t.target || idx} className="list-none">
              <RowCmp
                {...rowProps}
                className={cn(
                  "flex items-center justify-between gap-2 rounded-md border px-2.5 py-2 w-full text-left transition-colors",
                  isDiscovered ? "border-dashed border-border bg-muted/20" : "border-border bg-background/60",
                  rowClickable && "hover:bg-primary/5 hover:border-primary/30 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/40"
                )}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className={cn("w-2 h-2 rounded-full shrink-0", isDiscovered && "opacity-40")} style={{ background: meta.color }} />
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium truncate">{t.target}</div>
                    <div className="text-[10.5px] text-muted-foreground truncate">
                      {isDiscovered
                        ? <>Discovered{t.parent ? <> · via {t.parent}</> : null} · not scanned yet</>
                        : <>{t.total} finding{t.total === 1 ? "" : "s"}</>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {isDiscovered && canScan && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onScan?.(t.target); }}
                      disabled={scanning}
                      data-testid={`vuln-target-scan-${t.target}`}
                      className={cn(
                        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide transition-colors",
                        scanning
                          ? "border-border bg-muted text-muted-foreground cursor-wait"
                          : "border-primary/40 bg-primary/10 text-primary hover:bg-primary/20"
                      )}
                    >
                      {scanning
                        ? (<><Loader2 className="w-3 h-3 animate-spin" /> Queued</>)
                        : (<><Play className="w-3 h-3" /> Scan</>)}
                    </button>
                  )}
                  <span className={cn(
                    "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                    isDiscovered ? "border-border text-muted-foreground" : meta.ring
                  )}>
                    {isDiscovered ? "New" : meta.label}
                  </span>
                  {rowClickable && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                </div>
              </RowCmp>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


export default function PentestApp() {
  const [summary, setSummary] = useState(null);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanningHosts, setScanningHosts] = useState({});
  const [scanAllBusy, setScanAllBusy] = useState(false);
  const [wsMode, setWsMode] = useState("demo");
  const { toast } = useToast();
  const { user } = useAuth();
  const canScan = user?.role === "Super Admin"
    || (Array.isArray(user?.scopes) && user.scopes.includes("administrator"));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await api.get("/vulnerability/summary");
      setSummary(s.data);
      const rows = (s.data?.top_targets || []).map((t) => ({
        target: t.target,
        severity_counts: { critical: t.critical || 0, high: t.high || 0, medium: t.medium || 0, low: t.low || 0 },
        total: t.total,
        discovered_only: !!t.discovered_only,
        parent: t.parent || null,
      }));
      setTargets(rows);
      // A host stops "scanning" once it's promoted off discovered_only (has a real scan record).
      const stillDiscovered = new Set(rows.filter((r) => r.discovered_only).map((r) => r.target));
      setScanningHosts((prev) => {
        const next = {};
        for (const host of Object.keys(prev)) {
          if (stillDiscovered.has(host)) next[host] = true;
        }
        return next;
      });
    } catch {
      setSummary(null);
      setTargets([]);
    } finally { setLoading(false); }
  }, []);

  // Poll while a bulk "scan all discovered" sweep is in flight so rows promote live.
  const sweepStartedRef = React.useRef(false);

  useEffect(() => {
    if (!scanAllBusy) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [scanAllBusy, load]);

  useEffect(() => {
    if (scanAllBusy && sweepStartedRef.current && Object.keys(scanningHosts).length === 0) {
      setScanAllBusy(false);
      sweepStartedRef.current = false;
    }
  }, [scanAllBusy, scanningHosts]);

  const scanAllDiscovered = useCallback(async () => {
    if (scanAllBusy) return;
    setScanAllBusy(true);
    sweepStartedRef.current = false;
    try {
      const { data } = await api.post("/vulnerability/scan-all-discovered");
      const targetsQueued = data?.targets || [];
      if (targetsQueued.length === 0) {
        toast({ title: "Nothing to scan", description: "Every discovered subdomain already has a scan on record." });
        setScanAllBusy(false);
        return;
      }
      setScanningHosts((s) => {
        const next = { ...s };
        for (const host of targetsQueued) next[host] = true;
        return next;
      });
      sweepStartedRef.current = true;
      // Safety net: don't let the spinner spin forever if a queued host never promotes.
      setTimeout(() => setScanAllBusy(false), 5 * 60 * 1000);
      toast({
        title: "Bulk sweep queued",
        description: `${targetsQueued.length} discovered subdomains — passive TLS + headers scan running (max 5 at a time).`,
      });
      setTimeout(load, 4000);
    } catch (e) {
      toast({
        title: "Couldn't start bulk scan",
        description: e?.response?.data?.detail || "",
        variant: "destructive",
      });
      setScanAllBusy(false);
    }
  }, [scanAllBusy, toast, load]);

  const scanHost = useCallback(async (host) => {
    if (!host || scanningHosts[host]) return;
    setScanningHosts((s) => ({ ...s, [host]: true }));
    try {
      await api.post("/vulnerability/scan", { target: host, intensity: "passive" });
      toast({
        title: "Scan queued",
        description: `${host} — passive TLS + headers sweep running in background.`,
      });
      // Reload targets so the row promotes once findings appear.
      setTimeout(load, 4000);
    } catch (e) {
      toast({
        title: "Couldn't queue scan",
        description: e?.response?.data?.detail || "",
        variant: "destructive",
      });
      setScanningHosts((s) => { const n = { ...s }; delete n[host]; return n; });
    }
  }, [scanningHosts, toast, load]);

  useEffect(() => { load(); }, [load]);

  // ---- Drilldown state (findings modal + tab switching) ----
  const [drilldown, setDrilldown] = useState(null); // { title, description, filter }
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get("tab");
    return t === "workstations" || t === "agent" ? t : "scans";
  });
  const targetsCardRef = React.useRef(null);
  const scansSectionRef = React.useRef(null);

  const openFindings = useCallback((severity, extra = {}) => {
    const sevMap = {
      critical: "Critical severity findings",
      high: "High severity findings",
      medium: "Medium severity findings",
      low: "Low severity findings",
      info: "Informational findings",
    };
    if (severity && severity.includes(",")) {
      setDrilldown({
        title: "Critical & high findings",
        description: "Findings that need remediation within 7 days.",
        filter: { severity },
        ...extra,
      });
      return;
    }
    setDrilldown({
      title: severity ? sevMap[severity] : "All open findings",
      description: severity ? `Filtered to ${severity}.` : "All findings across every target and scan.",
      filter: severity ? { severity } : {},
      ...extra,
    });
  }, []);

  const openTargetFindings = useCallback((target) => {
    setDrilldown({
      title: target,
      description: "Findings for this target across all scans.",
      filter: { target },
    });
  }, []);

  const scrollToTargets = useCallback(() => {
    targetsCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const scrollToScans = useCallback((switchTab = true) => {
    if (switchTab) setActiveTab("scans");
    // wait for tab paint
    setTimeout(() => {
      scansSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }, []);

  const issues = summary?.severity_totals || { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
  const totalIssues = SEV_ORDER.reduce((a, k) => a + (issues[k] || 0), 0);
  const critHigh = (issues.critical || 0) + (issues.high || 0);
  const targetCount = summary?.kpis?.targets_scanned ?? 0;
  const activeScans = summary?.kpis?.active_scans ?? 0;
  const lastUpdate = summary?.kpis?.last_update;

  return (
    <div className="space-y-5" data-testid="pentest-app-page">
      <div className="text-[12px] text-muted-foreground inline-flex items-center gap-1 flex-wrap">
        <Link to="/" className="hover:text-foreground">Dashboard</Link>
        <span aria-hidden>›</span>
        <Link to="/apps" className="hover:text-foreground">Apps</Link>
        <span aria-hidden>›</span>
        <span className="text-foreground font-medium">Vulnerability</span>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
            <ShieldAlert className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <h1 className="text-[24px] font-semibold tracking-tight leading-tight">Vulnerability</h1>
            <p className="text-[13px] text-muted-foreground max-w-2xl mt-1">
              External attack-surface scanning for United Cloud — powered by UC Vigil&apos;s in-house scanner (Nuclei + Nmap + Nikto + Subfinder + TLS/headers audits).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="h-9 gap-1.5" data-testid="vuln-refresh-btn">
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label="Targets"
          value={targetCount}
          icon={Target}
          tone="primary"
          sub="Distinct hosts scanned"
          testId="vuln-kpi-targets"
          onClick={scrollToTargets}
        />
        <KpiCard
          label="Active scans"
          value={activeScans}
          icon={Activity}
          tone={activeScans > 0 ? "primary" : "emerald"}
          sub={activeScans > 0 ? "Running now" : "Idle — start one below"}
          testId="vuln-kpi-active-scans"
          onClick={() => scrollToScans(true)}
        />
        <KpiCard
          label="Open critical / high"
          value={critHigh}
          icon={ShieldAlert}
          tone={critHigh > 0 ? "rose" : "emerald"}
          sub={critHigh > 0 ? "Fix within 7 days" : "All clear"}
          testId="vuln-kpi-crit-high"
          onClick={critHigh > 0 ? () => openFindings("critical,high") : undefined}
        />
        <KpiCard
          label="Last update"
          value={lastUpdate ? relTime(lastUpdate) : "—"}
          icon={Clock}
          tone="primary"
          sub={lastUpdate ? fmtMDT(lastUpdate) : "No scans yet"}
          testId="vuln-kpi-updated"
          onClick={lastUpdate ? () => scrollToScans(true) : undefined}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <SeverityDonut
          issues={issues}
          total={totalIssues}
          onSeverityClick={totalIssues > 0 ? openFindings : undefined}
        />
        <TrendBar total={totalIssues} />
        <TopTargets
          targets={targets}
          loading={loading}
          canScan={canScan}
          onScan={scanHost}
          scanningHosts={scanningHosts}
          onOpenTarget={openTargetFindings}
          targetsCardRef={targetsCardRef}
          onScanAll={scanAllDiscovered}
          scanAllBusy={scanAllBusy}
        />
      </div>

      <div ref={scansSectionRef}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList data-testid="vuln-tabs-list">
            <TabsTrigger value="scans" data-testid="vuln-tab-scans">Deterministic scans</TabsTrigger>
            <TabsTrigger value="agent" data-testid="vuln-tab-agent">
              AI pen tester
              <span className="ml-1.5 text-[9.5px] rounded-full border border-primary/30 bg-primary/10 text-primary px-1.5 py-0.5 font-semibold uppercase tracking-wide">Beta</span>
            </TabsTrigger>
            <TabsTrigger value="workstations" data-testid="vuln-tab-workstations">
              Workstations
              <span className={cn(
                "ml-1.5 text-[9.5px] rounded-full border px-1.5 py-0.5 font-semibold uppercase tracking-wide",
                wsMode === "live" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"
              )}>
                {wsMode === "live" ? "Live" : "Demo"}
              </span>
            </TabsTrigger>
          </TabsList>
          <TabsContent value="scans" className="mt-3">
            <InHouseScans />
          </TabsContent>
          <TabsContent value="agent" className="mt-3">
            <AIPentester />
          </TabsContent>
          <TabsContent value="workstations" className="mt-3">
            <WorkstationScans onModeChange={setWsMode} />
          </TabsContent>
        </Tabs>
      </div>

      <FindingsDrilldown
        open={!!drilldown}
        onClose={() => setDrilldown(null)}
        title={drilldown?.title}
        description={drilldown?.description}
        filter={drilldown?.filter || {}}
      />
    </div>
  );
}
